import { Card, CardContent } from "@designs-by-maryam/ui/components/card";
import { cn } from "@designs-by-maryam/ui/lib/utils";
import { Award, GraduationCap, Sparkles } from "lucide-react";
import { motion } from "motion/react";
import { useSafeMotion } from "@/lib/motion";

type CredentialsSectionProps = {
	license: {
		title: string;
		category: string;
		issuer: string;
		date: string;
	};
	education: {
		degree: string;
		institution: string;
		year: string;
	};
	className?: string;
};

/**
 * CredentialsSection Component
 *
 * Displays professional credentials including CRPEP license and education.
 *
 * Features:
 * - Visually distinguished with gradient background
 * - Enhanced badge/certificate design with borders and shadows
 * - Animated entrance with stagger effect
 * - Badge decorations with shimmer effects
 * - Icon glow and depth effects
 * - Semantic description list for accessibility
 */
export function CredentialsSection({
	license,
	education,
	className,
}: CredentialsSectionProps) {
	const { shouldReduce, slideUp, scaleIn } = useSafeMotion();

	return (
		<section
			aria-labelledby="credentials-heading"
			className={cn(
				"relative overflow-hidden bg-gradient-to-b from-primary/5 to-background py-16 md:py-24 dark:from-primary/10 dark:to-background",
				className,
			)}
		>
			{/* Subtle Background Elements */}
			<div
				aria-hidden="true"
				className="pointer-events-none absolute inset-0 overflow-hidden"
			>
				<div className="absolute top-20 left-1/4 h-48 w-48 rounded-full bg-primary/5 blur-xl" />
				<div className="absolute right-1/4 bottom-20 h-48 w-48 rounded-full bg-purple-500/5 blur-xl" />
			</div>

			{/* Decorative Badge Pattern */}
			<div
				aria-hidden="true"
				className="absolute inset-0 opacity-[0.02]"
				style={{
					backgroundImage:
						"repeating-linear-gradient(45deg, currentColor 0, currentColor 1px, transparent 1px, transparent 12px)",
				}}
			/>

			<div className="container relative mx-auto px-4 md:px-6">
				{/* Section Header */}
				<motion.div
					animate={slideUp.animate}
					className="mb-12 text-center"
					initial={slideUp.initial}
					transition={slideUp.transition}
				>
					<div className="mb-4 flex justify-center">
						<Sparkles className="h-8 w-8 text-primary" />
					</div>
					<h2
						className="font-bold text-3xl text-foreground tracking-tight md:text-4xl"
						id="credentials-heading"
					>
						Professional Credentials
					</h2>
					<p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
						Licensed and qualified to deliver exceptional interior design
					</p>
				</motion.div>

				{/* Credentials Cards */}
				<div className="mx-auto grid max-w-4xl gap-6 md:grid-cols-2">
					{/* License Card */}
					<motion.div
						animate={scaleIn.animate}
						initial={scaleIn.initial}
						transition={{
							duration: shouldReduce ? 0.01 : 0.5,
							delay: shouldReduce ? 0 : 0.2,
						}}
					>
						<Card className="group hover:-translate-y-1 relative h-full overflow-hidden border-2 border-primary/20 bg-white shadow-lg transition-all duration-300 hover:border-primary/40 hover:shadow-xl dark:border-primary/30 dark:bg-card dark:hover:border-primary/50">
							{/* Gradient overlay on hover */}
							<div
								aria-hidden="true"
								className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/0 to-primary/0 opacity-0 transition-opacity duration-300 group-hover:from-primary/5 group-hover:to-primary/10 group-hover:opacity-100"
							/>

							{/* Badge ribbon effect */}
							<div
								aria-hidden="true"
								className="absolute top-0 right-0 h-16 w-16 overflow-hidden"
							>
								<div className="-top-2 -right-2 absolute h-20 w-20 rotate-45 bg-gradient-to-br from-primary/20 to-primary/10 transition-transform duration-300 group-hover:scale-110" />
							</div>

							{/* Shimmer effect */}
							<div
								aria-hidden="true"
								className="-translate-x-full absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-1000 group-hover:translate-x-full"
							/>

							<CardContent className="relative p-6">
								<div className="flex items-start gap-4">
									<motion.div
										className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary/15 to-primary/25 text-primary shadow-md transition-transform duration-300 group-hover:scale-110 dark:from-primary/20 dark:to-primary/30 dark:text-primary"
										whileHover={{ rotate: [0, -10, 10, 0] }}
									>
										<Award aria-hidden="true" className="h-8 w-8" />
										{/* Glow effect */}
										<div className="-z-10 absolute inset-0 rounded-full bg-primary/30 opacity-0 blur-lg transition-opacity duration-300 group-hover:opacity-100" />
									</motion.div>
									<dl className="flex-1">
										<dt className="font-bold text-foreground text-lg leading-tight">
											{license.title}
										</dt>
										<dd className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary text-sm dark:bg-primary/20 dark:text-primary">
											{license.category}
										</dd>
										<dd className="mt-3 text-muted-foreground text-sm">
											{license.issuer}
										</dd>
										<dd className="mt-1 font-medium text-muted-foreground text-sm">
											Issued: {license.date}
										</dd>
									</dl>
								</div>
							</CardContent>

							{/* Bottom accent border */}
							<div
								aria-hidden="true"
								className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-primary via-primary/80 to-primary"
							/>
						</Card>
					</motion.div>

					{/* Education Card */}
					<motion.div
						animate={scaleIn.animate}
						initial={scaleIn.initial}
						transition={{
							duration: shouldReduce ? 0.01 : 0.5,
							delay: shouldReduce ? 0 : 0.3,
						}}
					>
						<Card className="group hover:-translate-y-1 relative h-full overflow-hidden border-2 border-primary/20 bg-white shadow-lg transition-all duration-300 hover:border-primary/40 hover:shadow-xl dark:border-primary/30 dark:bg-card dark:hover:border-primary/50">
							{/* Gradient overlay on hover */}
							<div
								aria-hidden="true"
								className="pointer-events-none absolute inset-0 bg-gradient-to-br from-purple-500/0 to-primary/0 opacity-0 transition-opacity duration-300 group-hover:from-purple-500/5 group-hover:to-primary/10 group-hover:opacity-100"
							/>

							{/* Badge ribbon effect */}
							<div
								aria-hidden="true"
								className="absolute top-0 right-0 h-16 w-16 overflow-hidden"
							>
								<div className="-top-2 -right-2 absolute h-20 w-20 rotate-45 bg-gradient-to-br from-purple-500/20 to-primary/10 transition-transform duration-300 group-hover:scale-110" />
							</div>

							{/* Shimmer effect */}
							<div
								aria-hidden="true"
								className="-translate-x-full absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-1000 group-hover:translate-x-full"
							/>

							<CardContent className="relative p-6">
								<div className="flex items-start gap-4">
									<motion.div
										className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-100 to-primary/25 text-primary shadow-md transition-transform duration-300 group-hover:scale-110 dark:from-purple-900/50 dark:to-primary/30 dark:text-primary"
										whileHover={{ rotate: [0, -10, 10, 0] }}
									>
										<GraduationCap aria-hidden="true" className="h-8 w-8" />
										{/* Glow effect */}
										<div className="-z-10 absolute inset-0 rounded-full bg-purple-500/30 opacity-0 blur-lg transition-opacity duration-300 group-hover:opacity-100" />
									</motion.div>
									<dl className="flex-1">
										<dt className="font-bold text-foreground text-lg leading-tight">
											{education.degree}
										</dt>
										<dd className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary text-sm dark:bg-primary/20 dark:text-primary">
											{education.institution}
										</dd>
										<dd className="mt-3 font-medium text-muted-foreground text-sm">
											Graduated: {education.year}
										</dd>
									</dl>
								</div>
							</CardContent>

							{/* Bottom accent border */}
							<div
								aria-hidden="true"
								className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-purple-500 via-primary/80 to-primary"
							/>
						</Card>
					</motion.div>
				</div>
			</div>
		</section>
	);
}
